import React from 'react';
import Link from 'next/link';
import throttle from 'lodash/throttle';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useGsapContextSafe } from '@/utils/hooks/useGsapScroll';
import { trackClickEvent } from '@/utils/site-track';

const NavigationTabs = (props: {
  activeTabs: {
    title: string;
    en: string;
    dataEleId?: string;
    eleId?: string;
    href?: string;
  }[];
  selectedTab: string;
  setSelectedTab: React.Dispatch<React.SetStateAction<string>>;
  dataEleGroup?: string;
  sticky?: boolean;
  topOffset?: number;
  bottomOffset?: number;
  isFullScreen?: boolean;
  initObserverRef?: React.MutableRefObject<HTMLDivElement | null>;
  useRemainSpace?: boolean;
  keepActiveTab?: boolean;
}) => {
  const {
    selectedTab,
    setSelectedTab,
    activeTabs,
    dataEleGroup,
    sticky = true,
    topOffset,
    bottomOffset,
    isFullScreen,
    initObserverRef,
    useRemainSpace,
    keepActiveTab,
  } = props;
  const observerRef = React.useRef<HTMLDivElement | null>(null);

  const handleScroll = useGsapContextSafe();

  const handleChange = (e: React.SyntheticEvent, value: string) => {
    const tab = activeTabs.find((t) => t.en === value);
    if (tab?.href) return;

    setSelectedTab(value);
    dataEleGroup && trackClickEvent(e);
    if (!isFullScreen) {
      const ele = initObserverRef?.current || observerRef.current;
      const top = ele?.getBoundingClientRect().top || 0;
      const offsetY = topOffset || 0;
      if (ele && top <= offsetY) {
        handleScroll({
          scrollEle: ele,
          offsetY: topOffset || 0,
          duration: 0,
        });
      }
    } else {
      const tab = activeTabs.find((tab) => tab.en === value);
      if (tab?.eleId) {
        handleScroll({
          scrollId: tab.eleId,
          duration: 0,
          offsetY: topOffset || 0,
        });
      }
    }
  };

  React.useEffect(() => {
    if (!isFullScreen || !bottomOffset) return;

    const handleScroll = throttle(() => {
      let activeTab = '';
      for (const tab of activeTabs) {
        if (!tab.eleId) continue;
        const element = document.getElementById(tab.eleId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.bottom > bottomOffset) {
            activeTab = tab.en;
            break;
          }
        }
      }
      if (!keepActiveTab || activeTab) {
        setSelectedTab(activeTab);
      }
    }, 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {!initObserverRef && <div ref={observerRef} />}
      <Tabs
        className={`bg-web-grey-100 ${useRemainSpace ? 'grow' : 'grow-0'} rounded-xl p-1 z-10 ${sticky ? 'sticky top-0' : 'relative'} !min-h-0 [&_.MuiTabs-flexContainer]:gap-1`}
        value={selectedTab || false}
        onChange={handleChange}
        TabIndicatorProps={{
          className: '!bg-white !h-full rounded-lg -z-1',
        }}
      >
        {activeTabs.map((tab) => (
          <Tab
            key={tab.en}
            label={tab.title}
            value={tab.en}
            className={`flex-1 !p-2 !min-h-0${useRemainSpace ? '' : ' !min-w-40'} !text-base !leading-5.5 !font-semibold !text-web-grey-600 [&.Mui-selected]:!text-primary hover:!text-primary`}
            data-ele-id={tab.dataEleId}
            data-ele-group={dataEleGroup}
            {...(tab.href ? { component: Link, href: tab.href } : {})}
          />
        ))}
      </Tabs>
    </>
  );
};

export default NavigationTabs;
